import React, { useState, useRef } from "react";
import {
  CCard,
  CCardBody,
  CCol,
  CRow,
  CFormLabel,
  CFormInput,
  CButton,
  CFormSelect,
} from "@coreui/react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { uploadUserKyc } from "../../features/user/userSlice";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import AppToast from "../../components/seller/AppToast";

const validationSchema = Yup.object().shape({
  kyc_type: Yup.string().required("Verification type is required"),
  type: Yup.string().required("Document type is required"),
  custom_type: Yup.string().when("type", {
    is: "other",
    then: (schema) => schema.required("Please specify the document type"),
    otherwise: (schema) => schema.notRequired(),
  }),
  file: Yup.mixed().required("Document file is required"),
  is_consent: Yup.boolean()
    .required("You must agree to the is_consent.")
    .oneOf([true], "You must agree to the is_consent."),
});

const UploadKycForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { kycLoading } = useSelector((state) => state.user);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState("info");
  const [toastMessage, setToastMessage] = useState("");
  const fileRef = useRef(null);

  const initialValues = {
    kyc_type: "",
    type: "",
    custom_type: "",
    file: null,
    is_consent: false,
  };

  const documentOptions = {
    individual: [
      { value: "au_passport", label: "Australian Passport" },
      { value: "driver_license", label: "Driver Licence" },
      { value: "foreign_passport", label: "Foreign Passport" },
      { value: "medicare_card", label: "Medicare Card (Secondary ID)" },
      { value: "bank_statement", label: "Bank Statement (Address Proof)" },
      { value: "utility_bill", label: "Utility Bill (Address Proof)" },
      { value: "other", label: "Other" },
    ],
    company: [
      { value: "abn_extract", label: "ABN Extract / ABN Details" },
      { value: "asic_company_extract", label: "ASIC Company Extract" },
      {
        value: "certificate_incorporation",
        label: "Certificate of Incorporation",
      },
      { value: "gst_registration", label: "GST Registration (if applicable)" },
      { value: "director_id", label: "Director ID (if applicable)" },
      {
        value: "authorized_signatory_id",
        label: "Authorized Signatory ID (Passport / Licence)",
      },
      { value: "other", label: "Other" },
    ],
  };

  const handleFormikSubmit = async (values, { setSubmitting, resetForm }) => {
    const submission = new FormData();
    // console.log("Values submitted:", values);
    submission.append("kyc_type", values.kyc_type);
    submission.append(
      "document_type",
      values.type === "other" ? values.custom_type : values.type,
    );
    submission.append("document", values.file);
    submission.append("is_consent", values.is_consent ? 1 : 0);
    try {
      const res = await dispatch(uploadUserKyc(submission)).unwrap();
      setToastType("success");
      setToastMessage(res?.message || "Profile verification document uploaded successfully.");
      setToastVisible(true);
      setToastVisible(true);
      resetForm();
      if (fileRef.current) fileRef.current.value = "";
      setTimeout(() => {
        navigate(-1);
      }, 2000);
    } catch (err) {
      console.error("Upload failed:", err);
      setToastType("error");
      setToastMessage(err || "Failed to upload verification document.");
      setToastVisible(true);
    }
    setSubmitting(false);
  };

  return (
    <>
      <div className="container-fluid px-3">
        <CCard className="mt-4">
          <CCardBody>
            <CRow className="mb-4 align-items-center">
              <CCol>
                <h4 className="mb-0">Verify Profile Documents</h4>
              </CCol>
              <CCol className="text-end">
                <CButton color="secondary" onClick={() => navigate(-1)}>
                  Back
                </CButton>
              </CCol>
            </CRow>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleFormikSubmit}
            >
              {({
                values,
                setFieldValue,
                handleBlur,
                touched,
                errors,
                isSubmitting,
              }) => {
                const docList = documentOptions[values.kyc_type] || [];
                return (
                  <Form>
                    <div className="mb-3">
                      <CFormLabel htmlFor="kyc_type">Verification Type</CFormLabel>
                      <CFormSelect
                        id="kyc_type"
                        name="kyc_type"
                        value={values.kyc_type}
                        onChange={(e) =>
                          setFieldValue("kyc_type", e.target.value)
                        }
                        onBlur={handleBlur}
                        className={
                          touched.kyc_type && errors.kyc_type
                            ? "is-invalid"
                            : ""
                        }
                      >
                        <option value="">Select Verification Type</option>
                        <option value="individual">Individual</option>
                        <option value="company">Company</option>
                      </CFormSelect>
                      {touched.kyc_type && errors.kyc_type && (
                        <div className="invalid-feedback d-block">
                          {errors.kyc_type}
                        </div>
                      )}
                    </div>

                    {/* <div className="mb-3">
                    <CFormLabel htmlFor="type">Document Type</CFormLabel>
                    <CFormInput
                      id="type"
                      name="type"
                      type="text"
                      placeholder="Enter document type"
                      value={values.type}
                      onChange={(e) => setFieldValue("type", e.target.value)}
                      onBlur={handleBlur}
                    />
                    {touched.type && errors.type && (
                      <div className="text-danger">{errors.type}</div>
                    )}
                  </div> */}

                    <div className="mb-3">
                      <CFormLabel htmlFor="type">Document Type</CFormLabel>
                      <CFormSelect
                        id="type"
                        name="type"
                        value={values.type}
                        onChange={(e) => setFieldValue("type", e.target.value)}
                        onBlur={handleBlur}
                        disabled={!values.kyc_type}
                        className={
                          touched.type && errors.type ? "is-invalid" : ""
                        }
                      >
                        <option value="">
                          {values.kyc_type
                            ? "Select Document Type"
                            : "Select Verification Type First"}
                        </option>

                        {docList.map((doc) => (
                          <option key={doc.value} value={doc.value}>
                            {doc.label}
                          </option>
                        ))}
                      </CFormSelect>

                      {touched.type && errors.type && (
                        <div className="invalid-feedback d-block">
                          {errors.type}
                        </div>
                      )}
                    </div>

                    {values.type === "other" && (
                      <div className="mb-3">
                        <CFormLabel htmlFor="custom_type">
                          Specify Document Type
                        </CFormLabel>
                        <CFormInput
                          id="custom_type"
                          name="custom_type"
                          type="text"
                          placeholder="Enter document type"
                          value={values.custom_type}
                          onChange={(e) =>
                            setFieldValue("custom_type", e.target.value)
                          }
                          onBlur={handleBlur}
                          className={
                            touched.custom_type && errors.custom_type
                              ? "is-invalid"
                              : ""
                          }
                        />
                        {touched.custom_type && errors.custom_type && (
                          <div className="invalid-feedback d-block">
                            {errors.custom_type}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="mb-4">
                      <CFormLabel htmlFor="file">Upload Document</CFormLabel>
                      <CFormInput
                        type="file"
                        id="file"
                        name="file"
                        ref={fileRef}
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) =>
                          setFieldValue("file", e.currentTarget.files[0])
                        }
                        onBlur={handleBlur}
                      />
                      {touched.file && errors.file && (
                        <div className="text-danger">{errors.file}</div>
                      )}
                    </div>

                    <div
                      className="mb-4 p-3 rounded"
                      style={{
                        border: "2px solid #ffc107", // strong border
                        backgroundColor: "#fff8e1", // soft highlight background
                        boxShadow: "0 2px 6px rgba(0,0,0,0.05)", // subtle shadow
                      }}
                    >
                      <div className="d-flex align-items-start gap-2">
                        <input
                          type="checkbox"
                          id="is_consent"
                          name="is_consent"
                          className="form-check-input mt-1"
                          style={{ transform: "scale(1.2)" }} // bigger checkbox
                          checked={values.is_consent}
                          onChange={(e) =>
                            setFieldValue("is_consent", e.target.checked)
                          }
                        />

                        <label
                          htmlFor="is_consent"
                          className="form-check-label"
                          style={{
                            fontSize: "1rem",
                            color: "#222",
                            fontWeight: "600",
                            lineHeight: "1.5",
                          }}
                        >
                          {values.kyc_type === "company"
                            ? "I confirm that I am the authorised representative of the company to handle the transactions- sale, purchase, financial transaction etc."
                            : "I confirm that I am authorized to handle the transactions- sale, purchase, financial transaction etc."}
                        </label>
                      </div>

                      {touched.is_consent && errors.is_consent && (
                        <div className="text-danger mt-2 fw-semibold">
                          {errors.is_consent}
                        </div>
                      )}
                    </div>

                    <div>
                      <CButton
                        type="submit"
                        color="primary"
                        disabled={isSubmitting || kycLoading || !values.is_consent}
                      >
                        Upload
                      </CButton>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </CCardBody>
        </CCard>
      </div>
      <AppToast
        visible={toastVisible}
        setVisible={setToastVisible}
        type={toastType}
        message={toastMessage}
      />
    </>
  );
};

export default UploadKycForm;
